import {Box, Radio, Stack, Text} from '@mantine/core';
import React from 'react';

import SearchInput from 'search/inputs/SearchInput';

import {EventTriggerConfig} from '../RoutineTypes';
import {withIndefiniteArticle} from '../routineUtils';
import {UNAVAILABLE_FEATURE_TOOLTIP} from '../triggerConstants';
import RoutineSchemaSelector from './RoutineSchemaSelector';
import {RoutineSchemaOption, RoutineSchemaSource} from './routineSchemaSources';
import SelectableRadioCard from './SelectableRadioCard';
import {TargetScopeCapabilities, TriggerScope} from './types';

interface TargetScopeSectionProps {
  capabilities: TargetScopeCapabilities;
  config: Partial<EventTriggerConfig>;
  getCardClassName: (selected: boolean, disabled: boolean) => string;
  objectTypeLabel: string;
  pluralObjectTypeLabel: string;
  onChange: (updates: Partial<EventTriggerConfig>) => void;
  onScopeChange: (nextScope: TriggerScope) => void;
  scope: TriggerScope | undefined;
  searchInputContainerClassName: string;
  sectionClassName: string;
  selectDropdownClassName: string;
  selectedObjectSearchType: string;
  selectedObjectType: string;
  selectedSchemaSource: RoutineSchemaSource | null;
  schemaOptions: Array<RoutineSchemaOption>;
  schemasLoading: boolean;
  schemasError: boolean;
}

export default function TargetScopeSection({
  capabilities,
  config,
  getCardClassName,
  objectTypeLabel,
  pluralObjectTypeLabel,
  onChange,
  onScopeChange,
  scope,
  searchInputContainerClassName,
  sectionClassName,
  selectDropdownClassName,
  selectedObjectSearchType,
  selectedObjectType,
  selectedSchemaSource,
  schemaOptions,
  schemasLoading,
  schemasError,
}: TargetScopeSectionProps): JSX.Element {
  const normalizedObjectTypeLabel = objectTypeLabel.toLowerCase();
  const objectCreatedSpecificInstanceTooltip = `Unable to select a specific ${normalizedObjectTypeLabel} with this type of trigger`;

  return (
    <Stack spacing={4} className={sectionClassName}>
      <Text size="sm" weight={600}>
        Targets
      </Text>
      <Text size="xs" color="dimmed">
        What is the scope for this background agent
      </Text>

      <Radio.Group value={scope ?? ''}>
        <Stack spacing="sm">
          <SelectableRadioCard
            value="all"
            label={`All ${pluralObjectTypeLabel}`}
            description={`Run when any ${normalizedObjectTypeLabel} matches the specified trigger`}
            disabled={capabilities.scopeGroupDisabled}
            showTooltip={false}
            onSelect={() => {
              onScopeChange('all');
            }}
            cardClassName={getCardClassName(scope === 'all', capabilities.scopeGroupDisabled)}
          />
          <SelectableRadioCard
            value="schema"
            label={`A type of ${normalizedObjectTypeLabel}`}
            description={`Specify the specific type of ${normalizedObjectTypeLabel} that would trigger this background agent`}
            disabled={capabilities.scopeGroupDisabled || capabilities.schemaScopeDisabled}
            showTooltip={capabilities.showSchemaScopeUnavailableTooltip}
            tooltipLabel={UNAVAILABLE_FEATURE_TOOLTIP}
            onSelect={() => {
              onScopeChange('schema');
            }}
            cardClassName={getCardClassName(
              scope === 'schema',
              capabilities.scopeGroupDisabled || capabilities.schemaScopeDisabled
            )}
          >
            {scope === 'schema' && selectedSchemaSource != null && (
              <Box className={searchInputContainerClassName}>
                <RoutineSchemaSelector
                  selectedSchemaId={config.schema_id ?? null}
                  onChange={onChange}
                  schemas={schemaOptions}
                  loading={schemasLoading}
                  error={schemasError}
                  dropdownClassName={selectDropdownClassName}
                />
              </Box>
            )}
          </SelectableRadioCard>
          <SelectableRadioCard
            value="instance"
            label={`A specific ${normalizedObjectTypeLabel}`}
            description={`Select the specific instance of ${withIndefiniteArticle(normalizedObjectTypeLabel)} that would trigger this background agent`}
            disabled={capabilities.scopeGroupDisabled || capabilities.instanceScopeDisabled}
            showTooltip={capabilities.showObjectCreatedInstanceTooltip}
            tooltipLabel={objectCreatedSpecificInstanceTooltip}
            onSelect={() => {
              onScopeChange('instance');
            }}
            cardClassName={getCardClassName(
              scope === 'instance',
              capabilities.scopeGroupDisabled || capabilities.instanceScopeDisabled
            )}
          >
            {scope === 'instance' && selectedObjectType !== '' && (
              <Box className={searchInputContainerClassName}>
                <SearchInput
                  types={[selectedObjectSearchType]}
                  selected={config.object_ids ?? []}
                  onSelect={(id) => {
                    if (id != null && !(config.object_ids ?? []).includes(id)) {
                      onChange({object_ids: [...(config.object_ids ?? []), id]});
                    }
                  }}
                  onRemove={(id) => {
                    onChange({
                      object_ids: (config.object_ids ?? []).filter((existingId) => existingId !== id),
                    });
                  }}
                  hideSelectedResults={true}
                  isMulti={true}
                  placeholder={`Search for ${objectTypeLabel}...`}
                  dropLeft={true}
                />
              </Box>
            )}
          </SelectableRadioCard>
        </Stack>
      </Radio.Group>
    </Stack>
  );
}
